<template>
  <v-card>
    <v-banner
      icon="mdi-message-flash-outline"
      single-line
    >
      <h2>
        Automated Text
      </h2>

      <template
        v-if="canUseAutomatedText"
        #actions
      >
        <div class="d-flex">
          {{ toggleText }}
          <v-switch
            v-model="docketEntry.has_scheduled_text"
            class="ma-0 pa-0 pl-4"
            hide-details
          />
        </div>
      </template>
    </v-banner>

    <v-container fluid>
      <v-form
        ref="form"
        v-model="isFormValid"
        :disabled="!docketEntry.has_scheduled_text"
      >
        <v-row>
          <v-col cols="12">
            {{ automatedTextSummary }}
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="docketEntry.automated_text.message"
              filled
              auto-grow
              hide-details
              label="Text Message Content"
              :rules="[rules.automatedTextMessageMessage(docketEntry, docketEntry.automated_text)]"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="docketEntry.automated_text.recipients"
              filled
              chips
              deletable-chips
              multiple
              hide-selected
              label="Mobile Texting Record Parties *"
              no-data-text="No users available"
              :items="textableParties"
              :menu-props="{offsetY: true, offsetOverflow: true, transition: false}"
              :rules="[rules.required, rules.automatedTextMessageRecipients(docketEntry, docketEntry.automated_text)]"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <div
      v-if="!hasPermissions"
      class="overlay-parent d-flex justify-center"
    >
      <div class="pt-16">
        <v-alert
          border="left"
          max-width="600"
          outlined
          prominent
          text
          type="error"
        >
          <p>
            This docket entry has an automated text message that can only be triggered by a contributor and above.
          </p>
          <p class="mb-0">
            For more details contact your administrator.
          </p>
        </v-alert>
      </div>
    </div>

    <div
      v-else-if="!hasTextableParties"
      class="overlay-parent d-flex justify-center"
    >
      <div class="pt-16">
        <v-alert
          border="left"
          max-width="600"
          outlined
          prominent
          text
          type="error"
        >
          <p>
            None of the phone numbers associated with the Record Parties for this Record are capable of
            receiving text messages (indicated as "Landline", "Mobile-No text", or "Whatsapp").
          </p>
          <p>
            To send an automated text, you'll need to add at least one phone number that can receive texts
            ("Mobile-Texting", "Other", or type not specified).
          </p>
        </v-alert>
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { dateDisplayFormat, partyName } from '@/utilities'
import { filterTextablePhones } from '@/utilities/filter-functions'
import { rules } from '@/utilities/validation-rules'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { RECORD_NAMESPACE } from '@/store/record'

export default {
  name: 'DocketEntryEditorAutomatedText',

  data () {
    return {
      isFormValid: false,
      record: null,
      rules,
    }
  },

  computed: {
    automatedTextSendAt () {
      const interval = this.docketEntryType.automated_text_template?.automated_text_interval || 0
      const unit = this.docketEntryType.automated_text_template?.automated_text_interval_unit

      const intervalOccurence = interval > 0 ? 'after' : 'before'

      const actionDate = this.docketEntry.action_date ? dateDisplayFormat(this.docketEntry.action_date) : null
      const eventTimeStart = this.docketEntry.event_time_start

      const eventDate = actionDate || '(event date not defined yet)'
      const eventDateTime = eventTimeStart ? `${eventDate} ${eventTimeStart}` : eventDate

      return `${Math.abs(interval)} ${unit} ${intervalOccurence} ${eventDateTime}`
    },

    automatedTextSummary () {
      const parties = this.textableParties.reduce((res, party) => {
        if (this.docketEntry.automated_text.recipients.includes(party.value)) {
          const preferred = party.phones.find(ph => ph.preferred_text)

          if (preferred) {
            res.push(`${party.text} at ${preferred.phone_number}`)
          } else {
            const phoneNumbers = party.phones.map(p => p.phone_number).join(',')
            res.push(`${party.text} at ${phoneNumbers}`)
          }
        }
        return res
      }, [])

      return `Send automated text message ${this.automatedTextSendAt} to ${parties.join(', ')} with message:`
    },

    canUseAutomatedText () {
      return this.hasPermissions && this.hasTextableParties
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    docketEntryType () {
      return this.docketEntryTypes.find(det => det.id === this.docketEntry.docket_entry_type_id)
    },

    hasPermissions () {
      return this.currentUser.atleastContributor
    },

    hasTextableParties () {
      return this.textableParties.length > 0
    },

    isValid () {
      return !this.docketEntry?.has_scheduled_text || this.isFormValid
    },

    legalCase () {
      return this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties || []
    },

    textableParties () {
      return this.legalCaseParties.concat([
        { ...this.record?.record_holder_party, id: this.record?.record_holder_party_id },
        { ...this.record?.contact_party, id: this.record?.contact_party_id },
      ]).reduce((res, party) => {
        const phones = party?.phones?.filter(filterTextablePhones)
        if (!phones?.length) return res
        if (res.find(p => p.value === party.id)) return res

        res.push({
          phones: phones,
          text: partyName(party),
          value: party.id,
        })

        return res
      }, [])
    },

    toggleText () {
      return `Automated Text is ${this.docketEntry.has_scheduled_text ? 'enabled' : 'disabled'}`
    },

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCase',
    ]),

    ...mapGetters([
      'currentUser',
    ]),
  },

  watch: {
    docketEntryType: {
      handler (newVal, oldVal) {
        if (newVal?.id === oldVal?.id || (!oldVal && this.docketEntry?.automated_text?.id)) return
        this.docketEntry.automated_text.message = newVal?.automated_text_template?.automated_text_message || ''
      },

      immediate: true,
    },

    isValid: {
      handler (val) {
        this.$emit('update:valid', val)
      },

      immediate: true,
    },

    legalCase: {
      handler (val) {
        if (!val) return

        this.getRecordById(val.record_id).
          then(data => { this.record = data })
      },

      immediate: true,
    },
  },

  methods: {
    validate () {
      this.$refs.form.validate()
    },

    ...mapActions(RECORD_NAMESPACE, [
      'getRecordById',
    ]),
  },
}
</script>

<style scoped>
  .overlay-parent {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.8);
  }
</style>
